import { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";

const LISTING_REASONS = [
  { id: "scam", name: "Scam or asks for upfront payment" },
  { id: "prohibited", name: "Prohibited or illegal item" },
  { id: "misleading", name: "Misleading or fake" },
  { id: "duplicate", name: "Duplicate / spam" },
  { id: "offensive", name: "Offensive content" },
  { id: "other", name: "Something else" },
];

export const USER_REASONS = [
  { id: "scam", name: "Scammer or asked for upfront payment" },
  { id: "fake_profile", name: "Fake profile / impersonation" },
  { id: "harassment", name: "Abusive or harassing messages" },
  { id: "no_show", name: "Repeatedly didn't show up" },
  { id: "other", name: "Something else" },
];

export function ReportDialog({ listingId, user, onNeedLogin, endpoint, reasons = LISTING_REASONS, title = "Report listing", label = "Report this listing", testId = "detail-report-btn" }) {
  const url = endpoint || `/listings/${listingId}/report`;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(url, { reason, details });
      toast.success("Thanks — our team will review this report.");
      setOpen(false); setReason(""); setDetails("");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v && !user ? onNeedLogin() : setOpen(v))}>
      <DialogTrigger data-testid={testId} className="flex items-center gap-1.5 text-xs text-slate-400 transition-colors hover:text-red-300">
        <Flag className="h-3.5 w-3.5" /> {label}
      </DialogTrigger>
      <DialogContent data-testid="report-dialog" className="border-white/10 bg-[#173742] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">Reports are kept private from the person you report.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {reasons.map((r) => (
            <button key={r.id} data-testid={`report-reason-${r.id}`} onClick={() => setReason(r.id)} className={`w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-colors ${reason === r.id ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/5 hover:bg-white/10"}`}>{r.name}</button>
          ))}
        </div>
        <textarea data-testid="report-details-input" rows={3} maxLength={1000} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything else we should know? (optional)" className="field resize-none text-sm" />
        <button data-testid="report-submit-btn" disabled={!reason || busy} onClick={submit} className="btn-lime w-full py-3 text-sm">Send report</button>
      </DialogContent>
    </Dialog>
  );
}
